import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { getUserHandle, setUserHandle } from "./localStorageUtils";
import { RefreshRequest, RefreshResponse } from "../resources/types/authTypes";
import { refresh as refreshToken } from "../resources/useAuthResource";
import { config } from "./config";

export const api = axios.create({
  baseURL: config.apiBaseUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const user = getUserHandle();
    if (user && user.token && user.token.access_token) {
      config.headers["Authorization"] = `Bearer ${user.token.access_token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

let isRefreshing = false;
let refreshPromise: Promise<RefreshResponse> | null = null;

/**
 * Queue to hold requests that received a 401 while a token refresh is in progress.
 * Each entry contains resolve/reject functions for the queued request's promise.
 */
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

/**
 * Processes all requests in the failedQueue after a refresh attempt.
 * If refresh succeeded, retries all queued requests with the new token.
 * If refresh failed, rejects all queued requests with the error.
 * @param error - The error from the refresh attempt, if any
 * @param token - The new access token, if refresh succeeded
 */
function processQueue(error: unknown, token: string | null = null) {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
}

/**
 * Handles token refresh logic and request retrying for 401 responses.
 * Ensures only one refresh request is in-flight at a time. Queues all other 401s until refresh completes.
 * @param originalRequest - The Axios request config that triggered the 401
 * @param error - The original Axios error
 * @returns A promise resolving to the retried request or rejecting with an error
 */
async function handleTokenRefresh(
  originalRequest: InternalAxiosRequestConfig & { _retry?: boolean },
  error: AxiosError,
) {
  if (isRefreshing) {
    // If a refresh is already in progress, queue this request
    return new Promise((resolve, reject) => {
      failedQueue.push({
        resolve: (token) => {
          if (typeof token === "string" && originalRequest.headers) {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
          }
          resolve(api(originalRequest));
        },
        reject,
      });
    });
  }

  originalRequest._retry = true;
  isRefreshing = true;
  const handle = getUserHandle();

  // If the user handle has a refresh token, attempt to refresh
  if (handle?.token?.refresh_token) {
    // Create a new refresh request
    const refreshReq: RefreshRequest = {
      refresh_token: handle.token.refresh_token,
    };

    // Refresh the token
    refreshPromise = refreshToken(refreshReq)
      .then((response: RefreshResponse) => {
        // Update the user handle with the new token
        setUserHandle(response);
        api.defaults.headers["Authorization"] =
          `Bearer ${response.token.access_token}`;
        // Process the queue with the new token
        processQueue(null, response.token.access_token);
        return response;
      })
      .catch((refreshError) => {
        // If refresh fails, clear the user handle and process the queue with the error
        localStorage.clear();
        processQueue(refreshError, null);
        throw refreshError;
      })
      .finally(() => {
        isRefreshing = false;
        refreshPromise = null;
      });

    try {
      const response = await refreshPromise;
      if (originalRequest.headers) {
        originalRequest.headers["Authorization"] =
          `Bearer ${response.token.access_token}`;
      }
      return api(originalRequest);
    } catch (refreshError) {
      return Promise.reject(refreshError);
    }
  } else {
    // If no refresh token is available, clear local storage, clear the queue, and reject the request
    localStorage.clear();
    processQueue(error, null);
    return Promise.reject(error);
  }
}

/**
 * Axios response interceptor to handle 401 Unauthorized errors.
 * If a 401 is received, attempts to refresh the token and retry the request.
 * Ensures only one refresh request is made at a time, queues others.
 */
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };
    if (error?.response?.status === 401 && !originalRequest._retry) {
      // Todo: Add check for error and redirect to login if unable to refresh token
      return handleTokenRefresh(originalRequest, error);
    }
    return Promise.reject(error);
  },
);
